import { useLiveClient } from '@components/Live/LiveClientProvider'
import { PeekSpaceResponse, PeekUserResponse } from '@harmony/live-types'
import { useQuery } from '@tanstack/react-query'
import { env } from 'utils'

const STALE_TIME = 3 * 60 * 1000 // 3 minutes
const REFETCH_INTERVAL = 10 * 60 * 1000 // 10 minutes

/**
 * Hook to fetch live peek data for a space
 * Returns subscriber count, stage count, and listener count
 */
export function useLivePeekSpace(spaceId: string | undefined) {
    const { enabled } = useLiveClient()
    const liveWorkerUrl = env.VITE_LIVE_WORKER_URL

    return useQuery<PeekSpaceResponse>({
        queryKey: ['live-peek-space', spaceId],
        queryFn: async () => {
            if (!liveWorkerUrl || !spaceId) {
                throw new Error('Live worker URL or spaceId not available')
            }

            const response = await fetch(`${liveWorkerUrl}/peek/${spaceId}`)
            if (!response.ok) {
                const error = await response.json().catch(() => ({ error: 'Unknown error' }))
                throw new Error(error.error || `Failed to fetch peek data: ${response.status}`)
            }

            return response.json()
        },
        enabled: enabled && !!spaceId && !!liveWorkerUrl,
        staleTime: STALE_TIME,
        refetchInterval: REFETCH_INTERVAL,
        refetchOnWindowFocus: true,
        retry: 1,
    })
}

/**
 * Hook to fetch live peek data for a user in a space
 * Returns the user's live state (disconnected, connected, or live)
 */
export function useLivePeekUser(spaceId: string | undefined, userId: string | undefined) {
    const { enabled } = useLiveClient()
    const liveWorkerUrl = env.VITE_LIVE_WORKER_URL

    return useQuery<PeekUserResponse>({
        queryKey: ['live-peek-user', spaceId, userId],
        queryFn: async () => {
            if (!liveWorkerUrl || !spaceId || !userId) {
                throw new Error('Live worker URL, spaceId, or userId not available')
            }

            const response = await fetch(`${liveWorkerUrl}/peek/${spaceId}/user/${userId}`)
            if (!response.ok) {
                const error = await response.json().catch(() => ({ error: 'Unknown error' }))
                throw new Error(error.error || `Failed to fetch user peek data: ${response.status}`)
            }

            return response.json()
        },
        enabled: enabled && !!spaceId && !!userId && !!liveWorkerUrl,
        staleTime: STALE_TIME,
        refetchInterval: REFETCH_INTERVAL,
        refetchOnWindowFocus: true,
        retry: 1,
    })
}

/**
 * Check if live features are enabled
 */
export function useLiveEnabled(): boolean {
    const { enabled } = useLiveClient()
    return enabled
}
